import { Children, isValidElement, useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

const textOf = (node) => Children.toArray(node).map((c) => (typeof c === "string" || typeof c === "number" ? String(c) : isValidElement(c) ? textOf(c.props.children) : "")).join("");

const parse = (children, group = "") => Children.toArray(children).flatMap((c) => {
  if (!isValidElement(c)) return [];
  if (c.type === "optgroup") return parse(c.props.children, c.props.label);
  if (c.type === "option") {
    const label = textOf(c.props.children);
    return [{ value: c.props.value !== undefined ? String(c.props.value) : label, label, group, disabled: c.props.disabled }];
  }
  return parse(c.props.children, group);
});

// Drop-in replacement for <select>: same value/onChange(e.target.value)/<option>/<optgroup> API, with search.
export const SearchSelect = ({ value, onChange, children, className = "", disabled, ...rest }) => {
  const [open, setOpen] = useState(false);
  const options = useMemo(() => parse(children), [children]);
  const current = options.find((o) => o.value === String(value ?? ""));
  const groups = [...new Set(options.map((o) => o.group))];
  const pick = (v) => { onChange?.({ target: { value: v } }); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild disabled={disabled}>
        <button type="button" role="combobox" aria-expanded={open} className={`${className} flex items-center justify-between gap-2 text-left`} {...rest}>
          <span className={`truncate ${current && current.value !== "" ? "" : "opacity-60"}`}>{current?.label || "Select"}</span>
          <ChevronsUpDown size={14} className="shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[220px] border-white/10 bg-[#121722] p-0 text-[#F1F5F9]" align="start">
        <Command className="bg-transparent text-[#F1F5F9]" filter={(v, s) => (v.toLowerCase().includes(s.trim().toLowerCase()) ? 1 : 0)}>
          <CommandInput placeholder="Search…" className="text-sm" data-testid={rest["data-testid"] ? `${rest["data-testid"]}-search` : undefined} />
          <CommandList className="max-h-64">
            <CommandEmpty>No match found.</CommandEmpty>
            {groups.map((g) => (
              <CommandGroup key={g || "_"} heading={g || undefined} className="[&_[cmdk-group-heading]]:text-[#C5A059]">
                {options.filter((o) => o.group === g).map((o) => (
                  <CommandItem key={`${g}-${o.value}`} value={`${o.label} ${g}`} disabled={o.disabled} onSelect={() => pick(o.value)}
                    className="cursor-pointer text-sm data-[selected=true]:bg-white/10 data-[selected=true]:text-[#F1F5F9]">
                    <Check size={14} className={`mr-2 ${o.value === String(value ?? "") ? "opacity-100 text-[#C5A059]" : "opacity-0"}`} />
                    {o.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
